/**
 * ScenarioLibrary — küratörlü konu havuzu modal'ı.
 *
 * Wizard adımı 1'in (QueryComposer) yanında çağrılır. Kullanıcı kategori
 * seçer, kart üzerine tıklar, query + topology + agentCount tek hamlede
 * wizard state'ine yazılır ve modal kapanır.
 *
 * Tasarım kararları:
 *   • Radix Dialog: a11y, ESC/overlay-click kapatma, focus-trap.
 *   • Sol panel kategoriler, sağ panel kart grid'i — desktop-first.
 *   • Mobile: kategori chip'leri yatay scroll.
 *   • Glass + accent gradient — diğer surface'larla tutarlı.
 */
import { useMemo, useState } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { motion, useReducedMotion } from "framer-motion";
import {
  ArrowRight,
  Clock,
  Library,
  Sparkles,
  Users,
  X,
} from "lucide-react";

import { useDebateStore } from "../../store/debateStore";
import {
  SCENARIOS,
  SCENARIO_CATEGORIES,
  type Scenario,
  type ScenarioCategoryId,
} from "./scenarios";
import { cn } from "../common/cn";

const TOPOLOGY_LABELS: Record<string, string> = {
  "round-robin": "Tur-Tur",
  "cross-fire": "Çapraz Ateş",
  "moderator-led": "Moderatör Liderliğinde",
  "diverse-poles": "Karşıt Kutuplar",
  "solo-then-debate": "Önce Solo / Sonra Tartışma",
};

interface ScenarioLibraryProps {
  open: boolean;
  onOpenChange(open: boolean): void;
}

export function ScenarioLibrary({ open, onOpenChange }: ScenarioLibraryProps) {
  const setQuery = useDebateStore((s) => s.setQuery);
  const setTopology = useDebateStore((s) => s.setTopology);
  const setAgentCount = useDebateStore((s) => s.setAgentCount);
  const wizardStep = useDebateStore((s) => s.wizard.step);
  const setStep = useDebateStore((s) => s.setStep);

  const [active, setActive] = useState<ScenarioCategoryId>(
    SCENARIO_CATEGORIES[0]!.id,
  );
  const reduceMotion = useReducedMotion();

  const visible = useMemo(
    () => SCENARIOS.filter((s) => s.category === active),
    [active],
  );

  const onPick = (s: Scenario) => {
    setQuery(s.query);
    if (s.recommendedTopology) setTopology(s.recommendedTopology);
    if (typeof s.recommendedAgents === "number") {
      setAgentCount(s.recommendedAgents);
    }
    // Smart-advance: if user is on step 1, jump to step 2 (config) so the
    // momentum keeps going. They've already chosen the WHAT, next is HOW.
    if (wizardStep <= 1) setStep(2);
    onOpenChange(false);
  };

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay
          className="fixed inset-0 z-[80] bg-bg-base/70 backdrop-blur-sm data-[state=open]:animate-in data-[state=open]:fade-in"
        />
        <Dialog.Content
          aria-describedby={undefined}
          className={cn(
            "fixed left-1/2 top-1/2 z-[81] flex w-[min(96vw,72rem)] max-h-[88vh]",
            "-translate-x-1/2 -translate-y-1/2 flex-col overflow-hidden rounded-2xl",
            "border border-white/[0.08] bg-surface-1/95 shadow-[0_30px_80px_-20px_rgba(0,0,0,0.65)] backdrop-blur-2xl",
            "data-[state=open]:animate-in data-[state=open]:fade-in data-[state=open]:zoom-in-95",
          )}
        >
          <header className="flex items-start justify-between gap-3 border-b border-white/[0.06] px-5 py-4">
            <div className="flex items-start gap-3">
              <span className="grid h-9 w-9 place-items-center rounded-lg bg-[hsl(var(--accent)/0.15)] ring-1 ring-[hsl(var(--accent)/0.5)]">
                <Library className="h-4 w-4 text-accent-300" aria-hidden />
              </span>
              <div>
                <Dialog.Title className="text-base font-semibold text-fg-strong">
                  Senaryo Kütüphanesi
                </Dialog.Title>
                <p className="mt-0.5 text-xs text-fg-subtle">
                  Önceden tasarlanmış strateji, teknoloji ve yönetişim sorularıyla saniyeler içinde tartışmaya başla.
                </p>
              </div>
            </div>
            <Dialog.Close
              aria-label="Kapat"
              className="focus-ring -mr-1 inline-flex h-8 w-8 items-center justify-center rounded-md text-fg-subtle hover:bg-white/[0.06] hover:text-fg-strong"
            >
              <X className="h-4 w-4" aria-hidden />
            </Dialog.Close>
          </header>

          <div className="flex flex-1 min-h-0 flex-col md:flex-row">
            {/* Categories — left rail (md+) / horizontal chips (mobile) */}
            <nav
              aria-label="Kategoriler"
              className="flex shrink-0 gap-2 overflow-x-auto border-b border-white/[0.06] px-4 py-3 md:w-56 md:flex-col md:gap-1 md:overflow-y-auto md:overflow-x-hidden md:border-b-0 md:border-r md:px-3"
            >
              {SCENARIO_CATEGORIES.map((c) => {
                const isActive = active === c.id;
                const count = SCENARIOS.filter((s) => s.category === c.id).length;
                return (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => setActive(c.id)}
                    className={cn(
                      "focus-ring shrink-0 rounded-lg px-3 py-2 text-left transition",
                      isActive
                        ? "bg-[hsl(var(--accent)/0.15)] text-accent-300 ring-1 ring-[hsl(var(--accent)/0.5)]"
                        : "text-fg-muted hover:bg-white/[0.04] hover:text-fg-strong",
                    )}
                  >
                    <div className="flex items-center justify-between gap-3">
                      <span className="text-sm font-medium">{c.label}</span>
                      <span
                        className={cn(
                          "rounded-full px-1.5 py-0.5 text-[10px] tabular-nums",
                          isActive
                            ? "bg-[hsl(var(--accent)/0.25)] text-accent-200"
                            : "bg-surface-2/60 text-fg-subtle",
                        )}
                      >
                        {count}
                      </span>
                    </div>
                    <p className="mt-0.5 hidden text-[11px] text-fg-subtle md:block">
                      {c.hint}
                    </p>
                  </button>
                );
              })}
            </nav>

            {/* Cards grid — right pane */}
            <div className="flex-1 overflow-y-auto p-4 sm:p-5">
              <ul className="grid gap-3 sm:grid-cols-2">
                {visible.map((s, i) => (
                  <motion.li
                    key={s.id}
                    initial={reduceMotion ? false : { opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{
                      duration: 0.18,
                      delay: reduceMotion ? 0 : Math.min(0.04 * i, 0.24),
                      ease: "easeOut",
                    }}
                  >
                    <button
                      type="button"
                      onClick={() => onPick(s)}
                      className="focus-ring group flex h-full w-full flex-col gap-2 rounded-xl border border-white/[0.06] bg-surface-0/60 p-4 text-left transition hover:border-[hsl(var(--accent)/0.45)] hover:bg-surface-0/80"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <h3 className="text-[13.5px] font-semibold leading-snug text-fg-strong">
                          {s.title}
                        </h3>
                        <ArrowRight
                          className="h-4 w-4 shrink-0 text-fg-subtle opacity-0 transition group-hover:translate-x-0.5 group-hover:opacity-100"
                          aria-hidden
                        />
                      </div>
                      <p className="line-clamp-3 text-[12px] leading-relaxed text-fg-muted">
                        {s.query}
                      </p>
                      <div className="mt-1 flex flex-wrap items-center gap-2 border-t border-white/[0.04] pt-2 text-[10.5px] text-fg-subtle">
                        {s.recommendedTopology ? (
                          <span className="inline-flex items-center gap-1 rounded-full bg-surface-2/70 px-2 py-0.5">
                            <Sparkles className="h-3 w-3" aria-hidden />
                            {TOPOLOGY_LABELS[s.recommendedTopology] ??
                              s.recommendedTopology}
                          </span>
                        ) : null}
                        {typeof s.recommendedAgents === "number" ? (
                          <span className="inline-flex items-center gap-1 rounded-full bg-surface-2/70 px-2 py-0.5">
                            <Users className="h-3 w-3" aria-hidden />
                            {s.recommendedAgents} üye
                          </span>
                        ) : null}
                        {typeof s.estimatedMinutes === "number" ? (
                          <span className="inline-flex items-center gap-1 rounded-full bg-surface-2/70 px-2 py-0.5">
                            <Clock className="h-3 w-3" aria-hidden />~
                            {s.estimatedMinutes} dk
                          </span>
                        ) : null}
                      </div>
                    </button>
                  </motion.li>
                ))}
              </ul>
              {visible.length === 0 ? (
                <p className="mt-6 text-center text-sm text-fg-subtle">
                  Bu kategoride henüz senaryo yok.
                </p>
              ) : null}
            </div>
          </div>

          <footer className="flex items-center justify-between gap-3 border-t border-white/[0.06] bg-surface-0/40 px-5 py-3 text-xs text-fg-subtle">
            <span>
              {SCENARIOS.length} hazır senaryo · İçerikleri özelleştirebilirsin.
            </span>
            <kbd className="rounded border border-white/10 bg-surface-2/70 px-1.5 py-0.5 font-mono text-[10px]">
              Esc
            </kbd>
          </footer>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
